import React, {
  type PropsWithChildren,
  createContext,
  useContext,
  useCallback,
  useMemo,
  useState,
  useEffect,
} from 'react'
import { ViewportListRef } from 'react-viewport-list'

type ScrollableElement = {
  id: string
  position: number
}

type ScrollProviderState = {
  register: (id: string, position: number) => void
  unregister: (id: string) => void
  scrollIntoView: (id: string) => void
}

const ScrollProviderContext = createContext<ScrollProviderState | null>(null)

const add = (element: ScrollableElement) => (elements: ScrollableElement[]) => {
  return [...elements.filter((el) => el.id !== element.id), element]
}

const remove = (id: string) => (elements: ScrollableElement[]) => {
  return elements.filter((el) => el.id !== id)
}

const ScrollProvider = ({
  listRef,
  children,
}: PropsWithChildren<{ listRef: React.RefObject<ViewportListRef | null> }>) => {
  const [elements, setElements] = useState<ScrollableElement[]>([])

  const register = useCallback(
    (id: string, position: number) => {
      const element = { id, position }
      setElements(add(element))
    },
    [setElements]
  )

  const unregister = useCallback(
    (id: string) => {
      setElements(remove(id))
    },
    [setElements]
  )

  const scrollIntoView = useCallback(
    (id: string) => {
      const element = elements.find((el) => el.id === id)

      if (!element) return

      listRef.current?.scrollToIndex({
        index: element.position,
        alignToTop: true,
        offset: -(window.innerHeight * 0.3),
        prerender: 8,
      })
    },
    [elements, listRef]
  )

  const value = useMemo(
    () => ({
      register,
      unregister,
      scrollIntoView,
    }),
    [register, unregister, scrollIntoView]
  )

  return (
    <ScrollProviderContext.Provider value={value}>
      {children}
    </ScrollProviderContext.Provider>
  )
}

function useScroll() {
  const context = useContext(ScrollProviderContext)
  if (!context) {
    throw new Error('')
  }
  return context
}

function useScrollRegister(id: string, position: number) {
  const { register, unregister } = useScroll()
  useEffect(() => {
    register(id, position)
    return () => unregister(id)
  }, [id, position, register, unregister])
}

export { useScroll, useScrollRegister }
export default ScrollProvider
